import { Enquiry } from '../App/ListPage/ListSection/ShipmentList/ShipmentList';
import { ShipmentProp } from '../App/ListPage/ListSection/ShipmentList/ShipmentListItem/ShipmentListItem';
import type { AtlStatusValue, MultiShipmentPayload, Shipment, ShipmentPayload } from '../models/shipments';

/**
 * Transforms a Shipments API payload for use on Track UI and its shared components
 * @param shipmentPayload A payload as expected from Shipments API
 */
export const transformShipmentResponse = (shipmentPayload: ShipmentPayload): Shipment | null => {
  if (!shipmentPayload?.articles?.length) return null;
  const articles = shipmentPayload.articles.map((articlePayload) => {
    const { details, ...articleInfo } = articlePayload;
    const articleDetail = details[0];
    return {
      ...articleInfo,
      ...articleDetail,
      atlStatus: (articleInfo.atlStatus?.statusAttributeValue as AtlStatusValue) || null,
    };
  });

  return {
    ...shipmentPayload,
    articles,
  };
};

/**
 * Transforms a payload from Shipments API list and query endpoints for use on Track UI and its shared components
 * @param payload A payload as expected from Shipments API query and watchlist endpoint
 */
export const transformShipmentListResponse = (payload: MultiShipmentPayload): Shipment[] => {
  const shipmentList: Shipment[] = [];
  payload.forEach((shipment) => {
    const transformedShipmentResp = transformShipmentResponse(shipment.shipment);
    transformedShipmentResp && shipmentList.push(transformedShipmentResp);
  });
  return shipmentList;
};

export const MILESTONE_SORT_ORDER = [
  'ATTEMPTED DELIVERY',
  'AWAITING COLLECTION',
  'CONTACT SENDER',
  'RETURNING TO SENDER',
  'RETURNED',
  "IT'S COMING TODAY",
  "IT'S ON ITS WAY",
  'IN TRANSIT',
  'DESPATCHED',
  'POSSIBLE DELAY',
  "WE'VE GOT IT",
  'LABEL CREATED BY SENDER',
  "IT'S BEING PREPARED",
  'DEFAULT', // Any unexpected milestones
  'PROCESSING',
  'STARTED',
  'UNSUCCESSFUL PICKUP',
  'PENDING',
  'DELIVERED',
];

export interface ShipmentCompareParam {
  articles: ShipmentCompareArticle[] | null;
}

interface ShipmentCompareArticle {
  events: { milestone?: string | null; dateTime: number }[] | null;
  milestones: { name: string }[] | null;
  trackStatusOfArticle: string;
}

export const compareShipments = (firstShipment: ShipmentCompareParam, nextShipment: ShipmentCompareParam) => {
  if (!firstShipment?.articles?.length || !nextShipment?.articles?.length) return 0;
  const firstShipmentArticle = firstShipment?.articles[0];
  const nextShipmentArticle = nextShipment?.articles[0];
  const firstMilestone = _getLatestMilestone(firstShipmentArticle);
  const nextMilestone = _getLatestMilestone(nextShipmentArticle);

  let firstMilestoneIndex: number = MILESTONE_SORT_ORDER.indexOf(firstMilestone);
  if (firstMilestoneIndex < 0) firstMilestoneIndex = MILESTONE_SORT_ORDER.indexOf('DEFAULT');
  let secondMilestoneIndex: number = MILESTONE_SORT_ORDER.indexOf(nextMilestone);
  if (secondMilestoneIndex < 0) secondMilestoneIndex = MILESTONE_SORT_ORDER.indexOf('DEFAULT');
  const milestoneOrder = firstMilestoneIndex - secondMilestoneIndex;

  if (!firstShipmentArticle.events?.length || !nextShipmentArticle.events?.length) return milestoneOrder;
  const firstLatestEventTime = firstShipmentArticle?.events?.[0]?.dateTime || 0;
  const nextLatestEventTime = nextShipmentArticle?.events?.[0]?.dateTime || 0;
  const latestScanOrder = nextLatestEventTime - firstLatestEventTime;

  return milestoneOrder || latestScanOrder || 0;
};

const _getLatestMilestone = ({ events, milestones, trackStatusOfArticle }: ShipmentCompareArticle) => {
  return (events?.[0]?.milestone || milestones?.[0]?.name || trackStatusOfArticle || '').toUpperCase();
};

export const matchEnquiriesToShipment = (enquiries: Enquiry[], shipment: Shipment | ShipmentProp) => {
  const matchingCases: string[] = [];

  shipment.articles.forEach((article) => {
    const matchingEnquiry = enquiries?.find((enquiry) => enquiry.articleId === article.articleId);

    if (matchingEnquiry) {
      matchingCases.push(...matchingEnquiry.cases);
    }
  });

  return matchingCases;
};

export const findShipmentByIdentifier = (shipmentArray: Shipment[], trackingIds: string[]) => {
  const identifier = trackingIds.map((trackingId) => trackingId.toUpperCase());
  const shipment = shipmentArray.find((shipment) => {
    const matchConsignment = identifier.some((trackingId) => shipment.consignmentId.toUpperCase() === trackingId);
    if (matchConsignment) return matchConsignment;
    const { articles } = shipment;
    const matchArticle = articles.some(({ articleId, barcode }) => {
      const barcodeStr = barcode?.split('|')?.[0] ?? barcode ?? '';
      return identifier.includes(articleId.toUpperCase()) || identifier.includes(barcodeStr.toUpperCase());
    });
    return matchArticle;
  });
  return shipment ?? null;
};

export const getUniqueDuplicateIds = (
  trackingIds: string[],
  shipmentList: Shipment[]
): { uniqueIds: string[]; duplicates: string[] } => {
  const duplicateSet = new Set<string>(),
    uniqueIds: string[] = [];
  if (!shipmentList.length) return { uniqueIds: trackingIds, duplicates: [] };
  // For each tracking id, check each watchlist shipment, then check each article within the shipment
  trackingIds.forEach((id) => {
    const hasDuplicate = shipmentList.some((shipment) => {
      if (shipment.consignmentId.toUpperCase() === id) return duplicateSet.add(id);
      const { articles } = shipment;
      return articles.some((article) => {
        const { articleId, barcode } = article;
        const barcodeStr = barcode?.split('|')?.[0] ?? barcode;
        if (barcodeStr?.toUpperCase() === id)
          return duplicateSet.add(articles.length > 1 ? shipment.consignmentId.toUpperCase() : articleId.toUpperCase());
        if (articleId.toUpperCase() === id)
          return duplicateSet.add(articles.length > 1 ? shipment.consignmentId.toUpperCase() : id);
      });
    });
    if (!hasDuplicate) uniqueIds.push(id);
  });
  return { uniqueIds, duplicates: Array.from(duplicateSet) };
};
